import React, { useState, useEffect } from 'react';
import { 
  FaUserGraduate, 
  FaCheck, 
  FaTimes, 
  FaComments, 
  FaExclamationTriangle,
  FaFilter,
  FaSort,
  FaEdit,
  FaTrash,
  FaEye,
  FaCalendarAlt,
  FaClock,
  FaClipboardList,
  FaPaperPlane,
  FaReply
} from 'react-icons/fa';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, where, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import './ExpertSupportAdmin.css';

const ExpertSupportAdmin = () => {
  const { currentUser } = useAuth();
  const [supportRequests, setSupportRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // list, detail, reply
  const [experts, setExperts] = useState([]);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [adminReply, setAdminReply] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch support requests from Firestore
  useEffect(() => {
    const fetchSupportRequests = async () => {
      setIsLoading(true);
      try {
        const q = query(
          collection(db, 'supportRequests'),
          orderBy('submittedAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const requests = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          submittedAt: doc.data().submittedAt || new Date().toISOString(),
          replies: doc.data().replies || []
        }));
        setSupportRequests(requests);
        setFilteredRequests(requests);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching support requests:', err);
        setError('Failed to load support requests. Please try again.');
        setIsLoading(false);
      }
    };

    const fetchExperts = async () => {
      try {
        const q = query(
          collection(db, 'experts'),
          where('active', '==', true)
        );
        const querySnapshot = await getDocs(q);
        const expertsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // If no experts in database, add some mock data
        if (expertsData.length === 0) {
          const mockExperts = [
            {
              id: 'exp1',
              name: 'Dr. Emily Chen',
              specialization: 'Mathematics',
              rating: 4.9,
              responseTime: '~2 hours',
              active: true
            },
            {
              id: 'exp2',
              name: 'Prof. James Wilson',
              specialization: 'Computer Science',
              rating: 4.7,
              responseTime: '~1 hour',
              active: true
            },
            {
              id: 'exp3',
              name: 'Dr. Maria Rodriguez',
              specialization: 'Physics',
              rating: 4.8,
              responseTime: '~3 hours',
              active: true
            }
          ];
          setExperts(mockExperts);
        } else {
          setExperts(expertsData);
        }
      } catch (err) {
        console.error('Error fetching experts:', err);
      }
    };

    fetchSupportRequests();
    fetchExperts();
  }, []);

  // Apply filters and sorting
  useEffect(() => {
    let result = [...supportRequests];

    // Apply status filter
    if (filter !== 'all') {
      result = result.filter(request => request.status === filter);
    }

    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(request => 
        request.subject?.toLowerCase().includes(term) ||
        request.topic?.toLowerCase().includes(term) ||
        request.question?.toLowerCase().includes(term) ||
        request.userEmail?.toLowerCase().includes(term)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.submittedAt) - new Date(b.submittedAt);
          break;
        case 'urgency':
          const urgencyOrder = { 'low': 0, 'medium': 1, 'high': 2 };
          comparison = urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
          break;
        case 'subject':
          comparison = a.subject.localeCompare(b.subject);
          break;
        default:
          comparison = new Date(a.submittedAt) - new Date(b.submittedAt);
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredRequests(result);
  }, [supportRequests, filter, sortBy, sortOrder, searchTerm]);

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      const requestRef = doc(db, 'supportRequests', requestId);
      await updateDoc(requestRef, { 
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      setSupportRequests(prev => 
        prev.map(req => 
          req.id === requestId ? { ...req, status: newStatus, updatedAt: new Date().toISOString() } : req
        )
      );
    } catch (err) {
      console.error('Error updating request status:', err);
      setError('Failed to update request status. Please try again.');
    }
  };

  const assignExpert = async (requestId, expertId) => {
    try {
      const requestRef = doc(db, 'supportRequests', requestId);
      const expert = experts.find(exp => exp.id === expertId);
      
      await updateDoc(requestRef, { 
        status: 'assigned',
        expertId: expertId,
        expertName: expert.name,
        assignedAt: new Date().toISOString()
      });
      
      // Update local state
      setSupportRequests(prev => 
        prev.map(req => 
          req.id === requestId ? { 
            ...req, 
            status: 'assigned', 
            expertId, 
            expertName: expert.name,
            assignedAt: new Date().toISOString() 
          } : req
        )
      );
      
      setViewMode('list');
      setSelectedRequest(null);
    } catch (err) {
      console.error('Error assigning expert:', err);
      setError('Failed to assign expert. Please try again.');
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'supportRequests', requestId));
      
      // Update local state
      setSupportRequests(prev => prev.filter(req => req.id !== requestId));
      
      if (selectedRequest?.id === requestId) {
        setSelectedRequest(null);
        setViewMode('list');
      }
    } catch (err) {
      console.error('Error deleting request:', err);
      setError('Failed to delete request. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'assigned': return 'status-assigned';
      case 'in-progress': return 'status-in-progress';
      case 'completed': return 'status-completed';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-pending';
    }
  };

  // New function to handle admin replies
  const handleSubmitAdminReply = async () => {
    if (!adminReply.trim() || !selectedRequest) return;
    
    setIsSubmitting(true);
    
    try {
      const requestRef = doc(db, 'supportRequests', selectedRequest.id);
      
      // Create the reply object
      const reply = {
        id: Date.now().toString(),
        content: adminReply,
        isAdminReply: true,
        adminName: currentUser?.displayName || 'Admin',
        adminId: currentUser?.uid,
        timestamp: new Date().toISOString()
      };
      
      // Get existing replies or initialize empty array
      const existingReplies = selectedRequest.replies || [];
      
      // Update Firestore with the new reply
      await updateDoc(requestRef, {
        replies: [...existingReplies, reply],
        status: 'in-progress',
        lastRepliedAt: new Date().toISOString(),
        lastRepliedBy: 'admin',
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      const updatedRequest = {
        ...selectedRequest,
        replies: [...existingReplies, reply],
        status: 'in-progress',
        lastRepliedAt: new Date().toISOString(),
        lastRepliedBy: 'admin',
        updatedAt: new Date().toISOString()
      };
      
      setSupportRequests(prev => 
        prev.map(req => req.id === selectedRequest.id ? updatedRequest : req)
      );
      
      setSelectedRequest(updatedRequest);
      setAdminReply('');
      
      // Also log this interaction to a separate collection for admin activity tracking
      try {
        await addDoc(collection(db, 'adminActivity'), {
          type: 'expert_support_reply',
          requestId: selectedRequest.id,
          adminId: currentUser?.uid,
          adminName: currentUser?.displayName || 'Admin',
          timestamp: serverTimestamp(),
          details: {
            subject: selectedRequest.subject,
            userEmail: selectedRequest.userEmail
          }
        });
      } catch (logError) {
        console.error('Error logging admin activity:', logError);
        // Non-critical error, so we don't need to alert the user
      }
      
    } catch (err) {
      console.error('Error submitting reply:', err);
      alert('Failed to submit your reply. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="expert-support-loading">
        <div className="loading-spinner"></div>
        <p>Loading support requests...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="expert-support-error">
        <FaExclamationTriangle />
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  if (viewMode === 'detail' && selectedRequest) {
    return (
      <div className="expert-support-detail">
        <button className="back-button" onClick={() => setViewMode('list')}>
          &larr; Back to List
        </button>
        
        <div className="request-detail-header">
          <h3>{selectedRequest.subject} - {selectedRequest.topic}</h3>
          <span className={`status-badge ${getStatusBadgeClass(selectedRequest.status)}`}>
            {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
          </span>
        </div>
        
        <div className="request-detail-info">
          <div className="info-group">
            <label>Submitted By:</label>
            <p>{selectedRequest.userEmail}</p>
          </div>
          
          <div className="info-group">
            <label>Submitted On:</label>
            <p>{formatDate(selectedRequest.submittedAt)}</p>
          </div>
          
          <div className="info-group">
            <label>Support Type:</label>
            <p>{selectedRequest.supportTypeName}</p>
          </div>
          
          <div className="info-group">
            <label>Urgency:</label>
            <p className={`urgency-${selectedRequest.urgency}`}>
              {selectedRequest.urgency.charAt(0).toUpperCase() + selectedRequest.urgency.slice(1)}
            </p>
          </div>
          
          {selectedRequest.expertName && (
            <div className="info-group">
              <label>Assigned Expert:</label>
              <p>{selectedRequest.expertName}</p>
            </div>
          )}
        </div>
        
        <div className="request-detail-question">
          <h4>Question:</h4>
          <p>{selectedRequest.question}</p>
        </div>
        
        {selectedRequest.attachments && selectedRequest.attachments.length > 0 && (
          <div className="request-detail-attachments">
            <h4>Attachments:</h4>
            <ul>
              {selectedRequest.attachments.map((attachment, index) => (
                <li key={index}>
                  <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                    {attachment.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Display replies */}
        {selectedRequest.replies && selectedRequest.replies.length > 0 && (
          <div className="request-replies">
            <h4>Conversation:</h4>
            <div className="replies-container">
              {selectedRequest.replies.map((reply) => (
                <div 
                  key={reply.id} 
                  className={`reply-message ${reply.isAdminReply ? 'admin-reply' : 'user-reply'}`}
                >
                  <div className="reply-header">
                    <span className="reply-author">
                      {reply.isAdminReply ? reply.adminName : selectedRequest.userEmail}
                    </span>
                    <span className="reply-time">{formatDate(reply.timestamp)}</span>
                  </div>
                  <div className="reply-content">{reply.content}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Admin reply form */}
        <div className="admin-reply-form">
          <h4>Reply to User:</h4>
          <textarea 
            value={adminReply}
            onChange={(e) => setAdminReply(e.target.value)}
            placeholder="Type your response to the user here..."
            rows={4}
          ></textarea>
          <div className="reply-actions">
            <button 
              className="send-reply-btn"
              onClick={handleSubmitAdminReply}
              disabled={!adminReply.trim() || isSubmitting}
            >
              <FaPaperPlane /> {isSubmitting ? 'Sending...' : 'Send Reply'}
            </button>
          </div>
        </div>
        
        <div className="request-detail-actions">
          <div className="status-buttons">
            <button 
              className={`status-button ${selectedRequest.status === 'pending' ? 'active' : ''}`}
              onClick={() => handleStatusChange(selectedRequest.id, 'pending')}
            >
              Mark as Pending
            </button>
            <button 
              className={`status-button ${selectedRequest.status === 'in-progress' ? 'active' : ''}`}
              onClick={() => handleStatusChange(selectedRequest.id, 'in-progress')}
            >
              Mark In Progress
            </button>
            <button 
              className={`status-button ${selectedRequest.status === 'completed' ? 'active' : ''}`}
              onClick={() => handleStatusChange(selectedRequest.id, 'completed')}
            >
              Mark Completed
            </button>
            <button 
              className={`status-button ${selectedRequest.status === 'cancelled' ? 'active' : ''}`}
              onClick={() => handleStatusChange(selectedRequest.id, 'cancelled')}
            >
              Mark Cancelled
            </button>
          </div>
          
          <button 
            className="delete-button"
            onClick={() => deleteRequest(selectedRequest.id)}
          >
            Delete Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="expert-support-admin">
      <div className="support-controls">
        <div className="search-filter">
          <input
            type="text"
            placeholder="Search requests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          
          <div className="filter-dropdown">
            <FaFilter />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All Requests</option>
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div className="sort-dropdown">
            <FaSort />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date">Date</option>
              <option value="urgency">Urgency</option>
              <option value="subject">Subject</option>
            </select>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>
        
        <div className="request-stats">
          <div className="stat-item">
            <FaClipboardList />
            <span>Total: {supportRequests.length}</span>
          </div>
          <div className="stat-item pending">
            <FaExclamationTriangle />
            <span>Pending: {supportRequests.filter(r => r.status === 'pending').length}</span>
          </div>
          <div className="stat-item in-progress">
            <FaComments />
            <span>In Progress: {supportRequests.filter(r => r.status === 'in-progress').length}</span>
          </div>
          <div className="stat-item completed">
            <FaCheck />
            <span>Completed: {supportRequests.filter(r => r.status === 'completed').length}</span>
          </div>
        </div>
      </div>
      
      {filteredRequests.length === 0 ? (
        <div className="no-requests">
          <FaExclamationTriangle />
          <p>No support requests found.</p>
        </div>
      ) : (
        <div className="requests-table">
          <table>
            <thead>
              <tr>
                <th>Subject</th>
                <th>User</th>
                <th>Type</th>
                <th>Urgency</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map(request => (
                <tr key={request.id} className={request.replies?.length > 0 ? 'has-replies' : ''}>
                  <td className="request-subject">
                    <div className="subject-info">
                      <span className="subject-title">{request.subject}</span>
                      <span className="subject-topic">{request.topic}</span>
                      {request.replies?.length > 0 && (
                        <span className="reply-count">
                          <FaComments /> {request.replies.length}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{request.userEmail}</td>
                  <td>{request.supportTypeName}</td>
                  <td>
                    <span className={`urgency-${request.urgency}`}>
                      {request.urgency.charAt(0).toUpperCase() + request.urgency.slice(1)}
                    </span>
                  </td>
                  <td>{formatDate(request.submittedAt)}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(request.status)}`}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>
                  </td>
                  <td className="request-actions">
                    <button
                      className="view-button"
                      onClick={() => {
                        setSelectedRequest(request);
                        setViewMode('detail');
                        setAdminReply('');
                      }}
                      title="View details and reply"
                    >
                      <FaEye />
                    </button>
                    <button
                      className="reply-button"
                      onClick={() => {
                        setSelectedRequest(request);
                        setViewMode('detail');
                        setAdminReply('');
                        // Focus the reply textarea after a brief delay to allow the view to render
                        setTimeout(() => {
                          const textarea = document.querySelector('.admin-reply-form textarea');
                          if (textarea) textarea.focus();
                        }, 100);
                      }}
                      title="Quick reply"
                    >
                      <FaReply />
                    </button>
                    <button
                      className="delete-button"
                      onClick={() => deleteRequest(request.id)}
                      title="Delete request"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ExpertSupportAdmin; 